import { useEffect, useState } from 'react';
import { getConfigCatClient } from '../lib/configcat';
import { CONFIGCAT_MAINTENANCE_KEY } from '../config';

/**
 * Lee el flag de mantenimiento global desde ConfigCat — ÚNICA fuente de verdad.
 *
 * Es intencional que NO viva en Supabase: la pantalla de mantenimiento es justo
 * lo que querés mostrar cuando Supabase se cae, así que el flag debe estar en
 * infra independiente (ConfigCat entrega la config por CDN global). Ver ADR-135.
 *
 * FAIL-OPEN: el SDK devuelve el default `false` ante cualquier error o si el
 * flag no existe — un hipo de ConfigCat no debe encerrar a todos los usuarios.
 * (Además el SDK cachea la última config buena, así que un fallo transitorio de
 * red devuelve el último valor conocido, no el default.)
 *
 * Refresco: AutoPoll cada 30s (config del cliente) + re-lectura ante el hook
 * `configChanged` para aplicar el flip al instante, y `forceRefreshAsync()` al
 * volver el foco / recuperar red para que sea snappy cuando el usuario vuelve.
 */
export function useMaintenanceFlag(): boolean {
  const [maintenance, setMaintenance] = useState(false);

  useEffect(() => {
    const client = getConfigCatClient();
    let alive = true;

    const read = async () => {
      try {
        const value = await client.getValueAsync(
          CONFIGCAT_MAINTENANCE_KEY,
          false,
        );
        if (alive) setMaintenance(value === true);
      } catch {
        /* fail-open: mantenemos el último valor conocido */
      }
    };

    // Lectura inicial + suscripción a los cambios que detecta el AutoPoll.
    void read();
    const onConfigChanged = () => {
      void read();
    };
    client.on('configChanged', onConfigChanged);

    // Refresco inmediato cuando el usuario vuelve a la pestaña o recupera red.
    const refresh = () => {
      client.forceRefreshAsync().then(read).catch(() => {});
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);

    return () => {
      alive = false;
      client.off('configChanged', onConfigChanged);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('online', refresh);
    };
  }, []);

  return maintenance;
}
